import { awardEnrichmentApi } from 'components/loyalty/AwardEnrichmentsApi';
import { IAwardEnrichment } from 'components/loyalty/models/AwardEnrichment';
import {
    MESSAGE_MARKETING_AWARD_DELETE_ERROR,
    MESSAGE_MARKETING_AWARD_DELETE_SUCCESS
} from 'config/messages';
import { removeArrayItem, updateArrayItem } from 'lib/helpers';
import logger from 'lib/logger';
import React from 'react';
import { useDispatch } from 'react-redux';
import { MatchCodesActionType } from 'store/match-codes/matchCodesActionTypes';
import { enqueueSnackbar } from 'store/notifications/notificationsActions';

export const useExternalPerksMutation = ({
    awardEnrichments,
    indexToDelete,
    selectedToDuplicate,
    setAwardEnrichments,
    setIndexToDelete,
    setIsCreateOpen,
    handleResetEditSelection,
    handleResetDuplicateSelection
}: {
    awardEnrichments: IAwardEnrichment[];
    indexToDelete: number;
    selectedToDuplicate?: IAwardEnrichment | null;
    setIsCreateOpen: () => void;
    setAwardEnrichments: React.Dispatch<React.SetStateAction<IAwardEnrichment[]>>;
    setIndexToDelete: React.Dispatch<React.SetStateAction<number>>;
    handleResetEditSelection: () => void;
    handleResetDuplicateSelection: () => void;
}) => {
    const dispatch = useDispatch();

    const [isProcessing, setIsProcessing] = React.useState(false);

    const handleConfirmDelete = React.useCallback(async () => {
        const award = awardEnrichments[indexToDelete];

        if (award) {
            try {
                setIsProcessing(true);
                const res = await awardEnrichmentApi.deleteAward(award._id, { skipResponseBody: true });

                if (!res.ok) {
                    throw new Error(res.body.message);
                }

                setAwardEnrichments(values => removeArrayItem(values, indexToDelete));
                setIndexToDelete(-1);

                dispatch(enqueueSnackbar(MESSAGE_MARKETING_AWARD_DELETE_SUCCESS, { variant: 'success' }));
                dispatch({ type: MatchCodesActionType.RESET_CACHE });
            } catch (error) {
                logger.error(error.message);
                dispatch(enqueueSnackbar(MESSAGE_MARKETING_AWARD_DELETE_ERROR, { variant: 'error' }));
            }
            setIsProcessing(false);
        }
    }, [awardEnrichments, dispatch, indexToDelete, setAwardEnrichments, setIndexToDelete]);

    const handleCreateSubmit = React.useCallback(
        (award: IAwardEnrichment) => {
            setAwardEnrichments(awards => [
                ...awards,
                {
                    ...award,
                    id: award._id
                }
            ]);
            dispatch({ type: MatchCodesActionType.RESET_CACHE });
            if (selectedToDuplicate) {
                handleResetDuplicateSelection();
            } else {
                setIsCreateOpen();
            }
        },
        [selectedToDuplicate, setAwardEnrichments, setIsCreateOpen, handleResetDuplicateSelection, dispatch]
    );
    const handleUpdateSuccess = React.useCallback(
        (award: IAwardEnrichment) => {
            const indexToUpdate = awardEnrichments.findIndex(({ _id }) => _id === award._id);
            if (indexToUpdate !== -1) {
                setAwardEnrichments(awards => updateArrayItem(awards, indexToUpdate, award));
                handleResetEditSelection();
            }
            dispatch({ type: MatchCodesActionType.RESET_CACHE });
        },
        [awardEnrichments, handleResetEditSelection, setAwardEnrichments, dispatch]
    );

    return {
        isProcessing,
        handleCreateSubmit,
        handleUpdateSuccess,
        handleConfirmDelete
    };
};
